import config from "../config";

const PADDLE_SCRIPT_SRC = "https://cdn.paddle.com/paddle/v2/paddle.js";
const TXN_ID_PATTERN = /^txn_[A-Za-z0-9]+$/;

type PaddleCheckoutOpenOptions = {
  transactionId: string;
};

type PaddleGlobal = {
  Environment?: { set: (env: "sandbox" | "production") => void };
  Initialize: (options: { token: string }) => void;
  Checkout: { open: (options: PaddleCheckoutOpenOptions) => void };
};

declare global {
  interface Window {
    Paddle?: PaddleGlobal;
  }
}

let scriptPromise: Promise<void> | null = null;
let initializedToken: string | null = null;

export const extractPaddleTransactionId = (source: string | null | undefined): string | null => {
  if (!source) {
    return null;
  }

  const trimmed = source.trim();

  if (TXN_ID_PATTERN.test(trimmed)) {
    return trimmed;
  }

  try {
    const url = trimmed.startsWith("?")
      ? new URL(`https://local.invalid/${trimmed}`)
      : new URL(trimmed, "https://local.invalid");
    const fromQuery = url.searchParams.get("_ptxn");

    if (fromQuery && TXN_ID_PATTERN.test(fromQuery)) {
      return fromQuery;
    }
  } catch {
    // fall through to regex
  }

  const match = trimmed.match(/[?&]_ptxn=(txn_[A-Za-z0-9]+)/);

  return match?.[1] ?? null;
};

const loadPaddleScript = (): Promise<void> => {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("PADDLE_UNAVAILABLE"));
  }

  if (window.Paddle) {
    return Promise.resolve();
  }

  if (scriptPromise) {
    return scriptPromise;
  }

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${PADDLE_SCRIPT_SRC}"]`
    );

    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener(
        "error",
        () => reject(new Error("PADDLE_SCRIPT_LOAD_FAILED")),
        { once: true }
      );
      return;
    }

    const script = document.createElement("script");
    script.src = PADDLE_SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptPromise = null;
      reject(new Error("PADDLE_SCRIPT_LOAD_FAILED"));
    };
    document.head.appendChild(script);
  });

  return scriptPromise;
};

export const openPaddleCheckoutOverlay = async (
  transactionIdOrUrl: string
): Promise<void> => {
  const transactionId = extractPaddleTransactionId(transactionIdOrUrl);

  if (!transactionId) {
    throw new Error("PADDLE_TXN_MISSING");
  }

  const token = config.paddle.clientToken.trim();

  if (!token) {
    throw new Error("PADDLE_CLIENT_TOKEN_MISSING");
  }

  await loadPaddleScript();

  const paddle = window.Paddle;

  if (!paddle) {
    throw new Error("PADDLE_UNAVAILABLE");
  }

  if (initializedToken !== token) {
    if (config.paddle.env !== "production") {
      paddle.Environment?.set("sandbox");
    }

    paddle.Initialize({ token });
    initializedToken = token;
  }

  paddle.Checkout.open({ transactionId });
};
